import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button, Breadcrumbs, Breadcrumb, Spinner, Icon, EditableText, Card, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useAppStore } from '../stores/UnifiedStoreProvider';
import { BlockNoteEditorWrapper } from '../components/Mindmap/BlockNoteEditorWrapper';
import { SaveIndicator } from '../components/Mindmap/SaveIndicator';
import Comments from '../components/comments/Comments';
import useTheme from '../hooks/useTheme';
import api from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import './NodeDocPage.css';

/**
 * Node Document Page
 * Full-page view for editing a node's BlockNote document
 * Includes breadcrumbs showing the node hierarchy
 */
const NodeDocPage: React.FC = observer(() => {
  const { orgSlug, mindmapId, nodeId } = useParams<{ 
    orgSlug: string; 
    mindmapId: string; 
    nodeId: string; 
  }>();
  const [searchParams] = useSearchParams();
  const docId = searchParams.get('docId'); // Get specific document ID from query params
  
  const navigate = useNavigate();
  const appStore = useAppStore();
  const { mindmap, organization } = appStore;
  const { isDark } = useTheme();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mindmapLoaded, setMindmapLoaded] = useState(false);
  const [docTitle, setDocTitle] = useState('');
  const [isCommentsSidebarCollapsed, setIsCommentsSidebarCollapsed] = useState(false);

  // Check if node exists in store
  const node = mindmap.nodes.find(n => n.id === nodeId);
  const docs = mindmap.getNodeDocs(nodeId || '');
  // Get the specific document by ID, or fallback to first doc
  const primaryDoc = docId ? docs.find(d => d.id === docId) : docs[0];

  // Load mindmap data first if needed
  useEffect(() => {
    async function loadMindmapIfNeeded() {
      if (!appStore.isInitialized || !mindmapId) return;
      
      // Check if this mindmap is loaded
      if (mindmap.mindmapId !== mindmapId || mindmap.nodes.length === 0) {
        try {
          console.log('📥 Loading mindmap nodes for:', mindmapId);
          const response = await api.get(`/mindmaps/${mindmapId}`);
          if (response.data?.nodes) {
            mindmap.initializeData({
              nodes: response.data.nodes,
              edges: [],
              version: 0,
            });
            console.log('✅ Mindmap nodes loaded:', response.data.nodes.length);
          }
        } catch (err) {
          console.error('Error loading mindmap:', err);
        }
      }
      setMindmapLoaded(true);
    }
    
    loadMindmapIfNeeded();
  }, [appStore.isInitialized, mindmapId]);

  useEffect(() => {
    // Only load doc after mindmap is loaded
    // Reload when docId changes
    if (nodeId && appStore.isInitialized && mindmapLoaded) {
      loadDoc();
    }
  }, [nodeId, docId, appStore.isInitialized, mindmapLoaded]);
  
  // Update local title when document changes
  useEffect(() => {
    if (primaryDoc) {
      // Only set the document title, not the concatenated version
      setDocTitle(primaryDoc.title || '');
    }
  }, [primaryDoc?.id, primaryDoc?.title]);

  const loadDoc = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      let loadedDocs = docs;

      // Load documents from backend if not in store
      if (!loadedDocs || loadedDocs.length === 0) {
        loadedDocs = await mindmap.loadNodeDocs(nodeId || '');
      }

      // If docId is specified but not found, or no docs exist at all
      if (docId && !loadedDocs.find(d => d.id === docId)) {
        setError('Document not found');
      } else if (!docId && loadedDocs.length === 0) {
        // No docId specified and no docs exist - create one
        const createdDoc = await mindmap.createNodeDoc(nodeId || '');
        if (createdDoc) {
          // Update URL to include the new document ID
          navigate(`/${orgSlug}/mindmap/${mindmapId}/node/${nodeId}/doc?docId=${createdDoc.id}`, { replace: true });
          loadedDocs = [createdDoc];
        }
      } else if (!docId && loadedDocs.length > 0) {
        // No docId specified but docs exist - redirect to first doc
        navigate(`/${orgSlug}/mindmap/${mindmapId}/node/${nodeId}/doc?docId=${loadedDocs[0].id}`, { replace: true });
      }
      
      // Set initial title from document
      const currentDoc = docId ? loadedDocs.find(d => d.id === docId) : loadedDocs[0];
      if (currentDoc) {
        setDocTitle(currentDoc.title || '');
      }
    } catch (err) {
      console.error('Error loading doc:', err);
      setError('Failed to load document');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTitleChange = (newTitle: string) => {
    console.log('🏷️ [NodeDocPage] handleTitleChange called with:', newTitle);
    setDocTitle(newTitle);
    
    if (!primaryDoc || !nodeId) {
      console.warn('⚠️ [NodeDocPage] Cannot save title - no primaryDoc or nodeId');
      return;
    }
    
    console.log('💾 [NodeDocPage] Saving document title:', newTitle);
    console.log('💾 [NodeDocPage] Document ID:', primaryDoc.id);
    console.log('💾 [NodeDocPage] Node ID:', nodeId);
    
    // Trigger a save with the new title
    mindmap.saveNodeDoc(nodeId, primaryDoc.id, { title: newTitle })
      .then(() => {
        console.log('✅ [NodeDocPage] Title saved successfully');
      })
      .catch((err) => {
        console.error('❌ [NodeDocPage] Error saving title:', err);
      });
  };

  const handleDelete = async () => {
    if (!primaryDoc) return;
    
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await mindmap.deleteNodeDoc(nodeId || '', primaryDoc.id);
        
        // Check if there are other documents left
        const remainingDocs = mindmap.getNodeDocs(nodeId || '');
        if (remainingDocs.length > 0) {
          // Navigate to the first remaining document
          navigate(`/${orgSlug}/mindmap/${mindmapId}/node/${nodeId}/doc?docId=${remainingDocs[0].id}`);
        } else {
          // No more documents - navigate back to mindmap
          navigate(`/${orgSlug}/mindmap/${mindmapId}`);
        }
      } catch (err) {
        console.error('Error deleting doc:', err);
      }
    }
  };

  // Build breadcrumb path from node to root
  const buildBreadcrumbs = () => {
    if (!node) return [];
    
    const breadcrumbs: Array<{ id: string; label: string; isRoot: boolean }> = [];
    let currentNode = node;
    
    // Walk up the parent chain
    while (currentNode) {
      breadcrumbs.unshift({
        id: currentNode.id,
        label: currentNode.label || 'Untitled',
        isRoot: currentNode.isRoot || false,
      });
      
      if (currentNode.parentId) {
        const parentNode = mindmap.nodes.find(n => n.id === currentNode.parentId);
        if (parentNode) {
          currentNode = parentNode;
        } else {
          break;
        }
      } else {
        break;
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbPath = buildBreadcrumbs();

  // Get mindmap name
  const currentMindmap = appStore.organization.mindmaps?.find((m: any) => m.id === mindmapId);
  
  // Convert breadcrumbs to Blueprint format
  const breadcrumbItems = [
    {
      icon: IconNames.HOME,
      text: organization.currentOrganization?.name || 'Organization',
      onClick: () => navigate(`/${orgSlug}/mindmap`),
    },
    {
      icon: IconNames.DIAGRAM_TREE,
      text: currentMindmap?.name || 'Mindmap',
      onClick: () => navigate(`/${orgSlug}/mindmap/${mindmapId}`),
    },
    ...breadcrumbPath.map((item, index) => ({
      icon: item.isRoot ? IconNames.GLOBE_NETWORK : IconNames.BOX,
      text: item.label,
      onClick: index === breadcrumbPath.length - 1 
        ? undefined // Current node - no click
        : () => {
            // Navigate to mindmap and select this node
            navigate(`/${orgSlug}/mindmap/${mindmapId}`);
            mindmap.selectNode(item.id);
          },
      current: index === breadcrumbPath.length - 1,
    })),
    {
      icon: IconNames.DOCUMENT,
      text: 'Document',
      current: true,
    },
  ];

  // Show loading while app initializes
  if (!appStore.isInitialized) {
    return (
      <div className="node-doc-page-loading">
        <Spinner size={50} />
        <p>Loading...</p>
      </div>
    );
  }

  if (!node) {
    return (
      <div className="node-doc-page-error">
        <Icon icon={IconNames.ERROR} size={40} />
        <h3>Node not found</h3>
        <Button onClick={() => navigate(`/${orgSlug}/mindmap/${mindmapId}`)}>
          Back to Mindmap
        </Button>
      </div>
    );
  }

  // Get creator info from organization store
  const creator = primaryDoc && primaryDoc.createdBy && appStore.organization.members ? 
    appStore.organization.members.find((m: any) => m.id === primaryDoc.createdBy) : null;

  return (
    <div className={`node-doc-page ${isDark ? 'bp5-dark' : ''}`}>
      {/* Header with Breadcrumbs */}
      <div className="node-doc-page-header">
        <div className="node-doc-breadcrumbs">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        <div className="node-doc-header-actions">
          {primaryDoc && <SaveIndicator saveState={primaryDoc.saveState} lastSaved={primaryDoc.lastSaved} error={primaryDoc.saveError} />}
          
          <Button
            icon={isCommentsSidebarCollapsed ? IconNames.CHEVRON_LEFT : IconNames.CHEVRON_RIGHT}
            minimal
            onClick={() => setIsCommentsSidebarCollapsed(!isCommentsSidebarCollapsed)}
            title={isCommentsSidebarCollapsed ? "Show comments" : "Hide comments"}
          />
          
          {primaryDoc && !primaryDoc.isEmpty && (
            <Button
              icon={IconNames.TRASH}
              intent="danger"
              minimal
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
          
          <Button
            icon={IconNames.CROSS}
            minimal
            onClick={() => navigate(`/${orgSlug}/mindmap/${mindmapId}`)}
          >
            Close
          </Button>
        </div>
      </div>

      {/* Document Content */}
      <div className="node-doc-page-main">
        <div className="node-doc-page-content">
        {isLoading && (
          <div className="node-doc-page-loading">
            <Spinner size={50} />
            <p>Loading document...</p>
          </div>
        )}
        
        {error && (
          <div className="node-doc-page-error">
            <Icon icon={IconNames.ERROR} size={40} />
            <p>{error}</p>
            <Button onClick={loadDoc}>Retry</Button>
          </div>
        )}
        
        {!isLoading && !error && primaryDoc && (
          <div className="node-doc-editor-container">
            <div className="node-doc-editor-inner">
              {/* Document Title */}
              <div className="node-doc-title-section">
                <EditableText
                  className="node-doc-title"
                  value={docTitle}
                  onChange={setDocTitle}
                  onConfirm={handleTitleChange}
                  placeholder="Document Title"
                  multiline={false}
                />
                
                {/* Document Metadata */}
                <div className="node-doc-metadata">
                  {creator && (
                    <div className="doc-creator">
                      <Icon icon={IconNames.USER} size={12} />
                      <span>Created by {creator.email || creator.name}</span>
                    </div>
                  )}
                  {primaryDoc.createdAt && (
                    <div className="doc-created">
                      <Icon icon={IconNames.TIME} size={12} />
                      <span>{formatDistanceToNow(primaryDoc.createdAt, { addSuffix: true })}</span>
                    </div>
                  )}
                  {primaryDoc.plainTextContent && (
                    <div className="doc-word-count">
                      <Icon icon={IconNames.DOCUMENT} size={12} />
                      <span>{primaryDoc.plainTextContent.split(/\s+/).filter(w => w).length} words</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Editor */}
              <BlockNoteEditorWrapper
                nodeId={nodeId || ''}
                initialContent={primaryDoc.content}
                onContentChange={(content) => {
                  mindmap.updateNodeDocContent(nodeId || '', primaryDoc.id, content);
                }}
                enableComments={false}
                currentUserId={appStore.auth.user?.id}
                theme={isDark ? 'dark' : 'light'}
              />
            </div>
          </div>
        )}
        </div>
        
        {/* Comments Sidebar */}
        <div className={`node-doc-comments-sidebar ${isCommentsSidebarCollapsed ? 'collapsed' : ''}`}>
          {isCommentsSidebarCollapsed ? (
            <div className="comments-sidebar-collapsed">
              <Button
                icon={IconNames.CHEVRON_LEFT}
                minimal
                onClick={() => setIsCommentsSidebarCollapsed(false)}
                title="Expand comments"
              />
            </div>
          ) : (
            <div className="comments-sidebar-expanded">
              <div className="comments-sidebar-header">
                <h3>Comments</h3>
                <Button
                  icon={IconNames.CHEVRON_RIGHT}
                  minimal
                  small
                  onClick={() => setIsCommentsSidebarCollapsed(true)}
                  title="Collapse comments"
                />
              </div>
              <div className="comments-sidebar-content">
                {nodeId && <Comments nodeId={nodeId} />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default NodeDocPage;

